import { productionApi } from '~/api/production';

import { generateSlug } from '../../utils/slugUtils';

/**
 * Duplicates selected slices to multiple storefront configurations.
 * Groups configs by pack group to optimize item conversions, then applies converted slices to all configs.
 *
 * @param {Array} selectedSlices - Array of slice objects to duplicate
 * @param {Array} selectedConfigs - Array of config objects containing {id} to duplicate slices to
 * @returns {Promise<Array>} Array of updated config objects with slices merged
 */
export const duplicateSlicesToConfigs = async (selectedSlices, selectedConfigs) => {
    const configs = await getConfigs(selectedConfigs);

    const configsByPackGroup = groupConfigsByPackGroup(configs);

    const slicesByPackGroup = new Map();
    for (const packGroupID of configsByPackGroup.keys()) {
        const convertedSlices = await convertSlicesForPackGroup(selectedSlices, packGroupID);
        slicesByPackGroup.set(packGroupID, convertedSlices);
    }

    return mergeSlicesIntoConfigs(configs, slicesByPackGroup);
};

/**
 * Fetches storefront configurations by their IDs.
 *
 * @param {Array} configIds - Array of config objects containing {id} property
 * @returns {Promise<Array>} Array of storefront configuration API responses
 */
export const getConfigs = async (configIds) =>
    Promise.all(configIds.map((config) => productionApi.getStorefrontConfigurationByID(config.id)));

/**
 * Groups configurations by their pack group ID to optimize processing.
 *
 * @param {Array} configs - Array of config objects with data.storefrontConfiguration.packGroupID
 * @returns {Map} Map where keys are packGroupIDs and values are arrays of configs
 */
export const groupConfigsByPackGroup = (configs) => {
    const configsByPackGroup = new Map();

    configs.forEach((config) => {
        const { packGroupID } = config.data.storefrontConfiguration;
        if (!configsByPackGroup.has(packGroupID)) {
            configsByPackGroup.set(packGroupID, []);
        }
        configsByPackGroup.get(packGroupID).push(config);
    });

    return configsByPackGroup;
};

/**
 * Converts slices for a specific pack group by converting their item lists using external SKUs.
 * For slices without carousel items, returns cleaned slices with original items preserved.
 *
 * @param {Array} slices - Array of slice objects to convert
 * @param {string|number} packGroupID - Pack group ID to convert items for
 * @returns {Promise<Array>} Array of slices with converted items or original items preserved
 */
export const convertSlicesForPackGroup = async (slices, packGroupID) => {
    const productMap = buildProductMap(slices);

    if (Object.keys(productMap).length === 0) {
        return slices.map((slice) => cleanSliceWithItems(slice, slice.items));
    }

    const convertedItemsBySliceId = await productionApi.getSellableItemsByExternalSku(productMap, packGroupID);

    return applyConvertedItemsToSlices(slices, convertedItemsBySliceId.data.carousels);
};

/**
 * Builds a product map from slices that have carousel IDs and items.
 * Maps slice IDs to arrays of external SKUs for item conversion.
 *
 * @param {Array} slices - Array of slice objects
 * @returns {Object} Object mapping slice IDs to arrays of external SKUs
 */
export const buildProductMap = (slices) => {
    const productMap = {};

    slices.forEach((slice) => {
        if (slice.carouselID && slice.items && slice.items.length > 0) {
            productMap[slice.id] = slice.items.map((item) => item.externalSku || item.external_sku);
        }
    });

    return productMap;
};

/**
 * Creates a cleaned copy of a slice with specified items and resets IDs.
 * Removes the slice ID and carousel ID (if present) to prepare for duplication.
 * Generates a slug from the slice name if name exists.
 *
 * @param {Object} slice - Original slice object
 * @param {Array} items - Items to assign to the cleaned slice
 * @returns {Object} Cleaned slice object with items and reset IDs
 */
export const cleanSliceWithItems = (slice, items) => ({
    ...slice,
    id: null,
    ...(slice.carouselID && { carouselID: null }),
    ...(items && { items }),
    ...(slice.name && { slug: generateSlug(slice.name) }),
});

/**
 * Applies converted items to slices based on conversion results.
 * For slices with carousel IDs, uses converted items; otherwise preserves original items.
 *
 * @param {Array} slices - Original slices to process
 * @param {Object} convertedItemsBySliceId - Object mapping slice IDs to converted items
 * @returns {Array} Array of slices with appropriate items applied
 */
export const applyConvertedItemsToSlices = (slices, convertedItemsBySliceId) =>
    slices.map((slice) => {
        if (slice.carouselID) {
            const convertedItems = convertedItemsBySliceId[slice.id] || [];
            return cleanSliceWithItems(slice, convertedItems);
        }
        return cleanSliceWithItems(slice, slice.items || []);
    });

/**
 * Merges converted slices into their corresponding configurations.
 * Prepends the converted slices to the existing slices in each config.
 *
 * @param {Array} configs - Array of configuration objects
 * @param {Map} slicesByPackGroup - Map of pack group IDs to converted slices
 * @returns {Array} Array of configs with merged slices
 */
export const mergeSlicesIntoConfigs = (configs, slicesByPackGroup) =>
    configs.map((config) => {
        const { packGroupID } = config.data.storefrontConfiguration;
        const convertedSlices = slicesByPackGroup.get(packGroupID) ?? [];

        return {
            ...config,
            data: {
                ...config.data,
                storefrontConfiguration: {
                    ...config.data.storefrontConfiguration,
                    slices: [...convertedSlices, ...config.data.storefrontConfiguration.slices],
                },
            },
        };
    });

/**
 * Saves multiple storefront configurations to the server.
 *
 * @param {Array} configs - Array of config objects with data.storefrontConfiguration
 * @returns {Promise<Array>} Array of save operation results
 */
export const saveConfigs = async (configs) =>
    Promise.all(
        configs.map((config) => productionApi.saveStorefrontConfiguration(config.data.storefrontConfiguration, true)),
    );
